import { usePmsMetricsForDate, pmsScope, pmsPoint } from '@app/model/pricing-automation/service/PmsMetrics';
import React, { PropsWithChildren } from 'react';
import CompanyHotelAvailabilityStateWithComparisonOutput
	from '@app/model/market_state/CompanyHotelAvailabilityStateWithComparisonOutput';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import { TagState } from '@app/component/tag/TagState';

export type AvailabilityDailyStateContextType = {
	availabilityDailyState: CompanyHotelAvailabilityStateWithComparisonOutput | undefined,
	total: number,
	capacity?: number | null,
	findState: (companyHotel: CompanyHotel, value: number) => TagState,
	hasData: boolean,
	isLoading: boolean
}

export const AvailabilityDailyStateContext = React.createContext<AvailabilityDailyStateContextType>({
	availabilityDailyState: undefined,
	total: 0,
	findState: () => 'none',
	hasData: false,
	isLoading: false,
});

const AvailabilityDailyStateProvider: React.FunctionComponent<PropsWithChildren<{
	companyHotel: CompanyHotel,
	statDate: string
}>> = ({ companyHotel, statDate, children }) => {

	const query = usePmsMetricsForDate(companyHotel.id, statDate);
	const scope = pmsScope(query.data?.days.find(d => d.statDate === statDate)?.scopes);
	const current = pmsPoint(scope);
	const previous = pmsPoint(scope, companyHotel.settings.calendar.numberOfDaysForPriceParity ?? 7);
	const toState = (point: typeof current) => ({ statDate, creationDate: point?.capturedAt ?? '',
		items: (point?.rooms ?? []).filter(r => r.availability != null).map(r => ({ roomName: r.name, availability: r.availability!, hotelId: r.hotelId! })) });
	const data = current?.availability == null ? undefined : { statDate, current: toState(current), previous: previous?.availability == null ? undefined : toState(previous) };
	const isLoading = query.isPending;
	const isSuccess = query.isSuccess && !query.isError;

	const findState = (companyHotel: CompanyHotel, value: number): TagState => {
		const range = companyHotel.settings.calendar.availabilityWarningRange;

		if (value < range.from) return 'availabilityLowWarning';
		else if (value >= range.from && value < range.to) return 'none';
		else return 'availabilityHighWarning';
	};

	if (isLoading || !isSuccess) {
		return <AvailabilityDailyStateContext.Provider
			value={{ availabilityDailyState: undefined, total: 0, findState, hasData: false, isLoading }}>{children}</AvailabilityDailyStateContext.Provider>;
	}

	if (isSuccess && !!data && !!data.current) {
		const total = current!.availability!;
		return <AvailabilityDailyStateContext.Provider
			value={{ availabilityDailyState: data, total, capacity: scope?.capacity, findState, hasData: true, isLoading: false }}>{children}</AvailabilityDailyStateContext.Provider>;
	}

	return <AvailabilityDailyStateContext.Provider
		value={{ availabilityDailyState: undefined, total: 0, findState, hasData: false, isLoading: false }}>{children}</AvailabilityDailyStateContext.Provider>;
};

export default AvailabilityDailyStateProvider;
